import { useState, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { calculateDistance } from '../lib/utils';
import { toast } from '../components/ui/sonner';
import { detectMockLocation, validateCoordinates } from '../lib/mockLocationDetector';

// GPS Security Configuration
export const GPS_CONFIG = {
  MAX_ACCURACY_METERS: 100,
  WARNING_ACCURACY_METERS: 50,
  MAX_AGE_SECONDS: 0,
  TIMEOUT_SECONDS: 60,
  MIN_RETRIES: 3,
  REQUIRE_HIGH_ACCURACY: true,
  MULTI_SAMPLE_COUNT: 2,
  SAMPLE_INTERVAL_MS: 1000,
  MAX_TELEPORT_SPEED_KMH: 200,
  MIN_ALTITUDE_VARIANCE: 0.1,
  LOCATION_HISTORY_SIZE: 5,
  SUSPICIOUS_PERFECT_ACCURACY: 1,
  MOCK_BLOCK_THRESHOLD: 50, // Block attendance if native mock riskScore >= this
};

const validateGpsAccuracy = (accuracy) => {
  if (accuracy > GPS_CONFIG.MAX_ACCURACY_METERS) {
    return {
      valid: false,
      message: `Akurasi GPS terlalu rendah (${Math.round(accuracy)}m). Maksimal ${GPS_CONFIG.MAX_ACCURACY_METERS}m. Coba di tempat terbuka.`
    };
  }
  if (accuracy > GPS_CONFIG.WARNING_ACCURACY_METERS) {
    return {
      valid: true,
      warning: `Akurasi GPS kurang optimal (${Math.round(accuracy)}m). Disarankan < ${GPS_CONFIG.WARNING_ACCURACY_METERS}m.`
    };
  }
  return { valid: true, message: null };
};

const calculateDistanceMeters = (lat1, lon1, lat2, lon2) => {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

/**
 * Get a single GPS position using Capacitor native or browser API.
 */
const getPositionNative = async () => {
  const { Geolocation } = await import('@capacitor/geolocation');
  const pos = await Geolocation.getCurrentPosition({
    enableHighAccuracy: true,
    timeout: 15000,
    maximumAge: 0,
  });
  return {
    coords: {
      latitude: pos.coords.latitude,
      longitude: pos.coords.longitude,
      accuracy: pos.coords.accuracy,
      altitude: pos.coords.altitude,
      altitudeAccuracy: pos.coords.altitudeAccuracy,
      heading: pos.coords.heading,
      speed: pos.coords.speed,
    },
    timestamp: pos.timestamp,
  };
};

const getPositionWeb = () => {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          coords: {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            altitude: position.coords.altitude,
            altitudeAccuracy: position.coords.altitudeAccuracy,
            heading: position.coords.heading,
            speed: position.coords.speed,
          },
          timestamp: position.timestamp,
        });
      },
      (error) => reject(error),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  });
};

/**
 * Unified GPS hook that works on both native (Capacitor) and web platforms.
 * On native, it uses @capacitor/geolocation. On web, it uses navigator.geolocation.
 * All security checks (fake GPS detection, accuracy validation) are preserved.
 */
export const useGpsLocation = ({ isMobileDevice, targetLocation }) => {
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [gpsRetryCount, setGpsRetryCount] = useState(0);
  const [gpsWarning, setGpsWarning] = useState(null);
  const [locationTimestamp, setLocationTimestamp] = useState(null);
  const [fakeGpsWarning, setFakeGpsWarning] = useState(null);
  const [fakeGpsIndicators, setFakeGpsIndicators] = useState([]);
  const [locationHistory, setLocationHistory] = useState([]);
  const [distance, setDistance] = useState(null);
  const [inRadius, setInRadius] = useState(false);
  const [mockDetection, setMockDetection] = useState(null);

  const isNative = Capacitor.isNativePlatform();

  const analyzeFakeGpsIndicators = useCallback((samples, currentPosition) => {
    const indicators = [];

    if (currentPosition.coords.accuracy < GPS_CONFIG.SUSPICIOUS_PERFECT_ACCURACY) {
      indicators.push({
        type: 'perfect_accuracy',
        severity: 'medium',
        message: `Akurasi sangat tinggi (${currentPosition.coords.accuracy.toFixed(1)}m) - periksa jika mencurigakan`
      });
    }

    if (!isMobileDevice && currentPosition.coords.accuracy < 10) {
      indicators.push({
        type: 'desktop_gps',
        severity: 'medium',
        message: 'Akurasi tinggi dari perangkat non-mobile - verifikasi manual diperlukan'
      });
    }

    if (samples.length >= 2) {
      for (let i = 1; i < samples.length; i++) {
        const prev = samples[i-1];
        const curr = samples[i];
        const distanceM = calculateDistanceMeters(
          prev.coords.latitude, prev.coords.longitude,
          curr.coords.latitude, curr.coords.longitude
        );
        const timeDiffSeconds = (curr.timestamp - prev.timestamp) / 1000;
        if (timeDiffSeconds > 0) {
          const speedKmh = (distanceM / 1000) / (timeDiffSeconds / 3600);
          if (speedKmh > GPS_CONFIG.MAX_TELEPORT_SPEED_KMH) {
            indicators.push({
              type: 'teleportation',
              severity: 'high',
              message: `Perpindahan cepat: ${distanceM.toFixed(0)}m dalam ${timeDiffSeconds.toFixed(1)}s`
            });
          }
        }
      }
    }

    return indicators;
  }, [isMobileDevice]);

  const collectGpsSamples = useCallback(async () => {
    const samples = [];
    const getPos = isNative ? getPositionNative : getPositionWeb;

    for (let i = 0; i < GPS_CONFIG.MULTI_SAMPLE_COUNT; i++) {
      try {
        const pos = await getPos();
        samples.push(pos);
        if (i < GPS_CONFIG.MULTI_SAMPLE_COUNT - 1) {
          await new Promise(r => setTimeout(r, GPS_CONFIG.SAMPLE_INTERVAL_MS));
        }
      } catch (error) {
        if (samples.length > 0) break;
        throw error;
      }
    }

    if (samples.length === 0) {
      throw new Error('No GPS samples collected');
    }
    return samples;
  }, [isNative]);

  const getLocation = useCallback(async (retryCount = 0) => {
    setGettingLocation(true);
    setLocationError(null);
    setGpsWarning(null);
    setGpsRetryCount(retryCount);
    setFakeGpsIndicators([]);

    // Check for geolocation support
    if (!isNative && !navigator.geolocation) {
      setLocationError('Geolocation tidak didukung browser ini');
      setGettingLocation(false);
      return;
    }

    setLocation(null);

    try {
      // Request permission on native
      if (isNative) {
        try {
          const { Geolocation } = await import('@capacitor/geolocation');
          const permStatus = await Geolocation.checkPermissions();
          if (permStatus.location !== 'granted' && permStatus.coarseLocation !== 'granted') {
            const reqResult = await Geolocation.requestPermissions();
            if (reqResult.location !== 'granted' && reqResult.coarseLocation !== 'granted') {
              setLocationError('Izin lokasi ditolak. Aktifkan GPS di pengaturan.');
              setGettingLocation(false);
              return;
            }
          }
        } catch (permErr) {
          console.warn('[GPS] Permission check failed:', permErr);
        }
      }

      toast.info('Mengumpulkan data GPS...', { duration: 2000 });
      const samples = await collectGpsSamples();
      const position = samples[samples.length - 1];
      const accuracy = position.coords.accuracy;
      const timestamp = position.timestamp;

      const now = Date.now();
      const ageSeconds = (now - timestamp) / 1000;
      if (ageSeconds > 30 && retryCount < GPS_CONFIG.MIN_RETRIES) {
        toast.warning('Data GPS lama terdeteksi, mencoba ulang...');
        setTimeout(() => getLocation(retryCount + 1), 1000);
        return;
      }

      const validation = validateGpsAccuracy(accuracy);
      if (!validation.valid) {
        if (retryCount < GPS_CONFIG.MIN_RETRIES) {
          toast.warning(`Akurasi ${Math.round(accuracy)}m, mencoba GPS lebih akurat...`);
          setTimeout(() => getLocation(retryCount + 1), 2000);
          return;
        }
        setLocationError(validation.message);
        setGettingLocation(false);
        return;
      }
      if (validation.warning) setGpsWarning(validation.warning);

      const indicators = analyzeFakeGpsIndicators(samples, { coords: position.coords, timestamp });

      // --- Native Mock Location Detection (Android only) ---
      let nativeMockResult = null;
      if (isNative) {
        try {
          nativeMockResult = await detectMockLocation();
          setMockDetection(nativeMockResult);

          // Merge native indicators into our indicator list
          if (nativeMockResult.indicators && nativeMockResult.indicators.length > 0) {
            for (const nativeInd of nativeMockResult.indicators) {
              indicators.push({
                type: nativeInd.type || 'native_mock',
                severity: nativeInd.severity || 'high',
                message: nativeInd.message || 'Deteksi mock GPS native',
              });
            }
          }

          // Also validate the coordinates through native layer
          const coordValidation = await validateCoordinates(
            position.coords.latitude,
            position.coords.longitude,
            position.coords.accuracy
          );
          if (coordValidation.flags && coordValidation.flags.length > 0) {
            for (const flag of coordValidation.flags) {
              indicators.push({
                type: flag.type || 'coord_validation',
                severity: flag.severity || 'medium',
                message: flag.message || 'Validasi koordinat gagal',
              });
            }
          }
        } catch (mockErr) {
          console.warn('[GPS] Mock detection failed:', mockErr);
        }
      } else {
        setMockDetection(null);
      }

      setFakeGpsIndicators(indicators);

      if (indicators.length > 0) {
        const criticalIndicators = indicators.filter(i => i.severity === 'critical' || i.severity === 'high');
        setFakeGpsWarning(indicators.map(i => i.message).join('. '));

        // Block attendance if native mock risk is too high
        if (nativeMockResult && nativeMockResult.riskScore >= GPS_CONFIG.MOCK_BLOCK_THRESHOLD) {
          toast.error('DIBLOKIR: Fake GPS terdeteksi! Nonaktifkan aplikasi Fake GPS untuk melanjutkan absensi.', {
            duration: 10000,
            description: `Risk score: ${nativeMockResult.riskScore}/100. ${nativeMockResult.mockAppsFound > 0 ? `Ditemukan ${nativeMockResult.mockAppsFound} app Fake GPS.` : ''}`
          });
        } else if (criticalIndicators.length > 0) {
          toast.error('PERINGATAN KRITIS: Terdeteksi indikasi kuat Fake GPS!', { duration: 7000, description: criticalIndicators[0].message });
        } else {
          toast.warning('PERINGATAN: Terdeteksi indikasi kemungkinan Fake GPS', { duration: 5000 });
        }
      } else {
        setFakeGpsWarning(null);
        if (isNative && nativeMockResult?.isSafe) {
          toast.success('GPS terverifikasi (Native Anti-Mock OK)', { duration: 2000 });
        } else {
          toast.success('GPS terverifikasi', { duration: 2000 });
        }
      }

      const loc = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy,
        timestamp,
        altitude: position.coords.altitude,
        altitudeAccuracy: position.coords.altitudeAccuracy,
        heading: position.coords.heading,
        speed: position.coords.speed,
        sampleCount: samples.length,
        accuracyVariance: samples.length > 1
          ? Math.sqrt(samples.reduce((sum, s) => sum + Math.pow(s.coords.accuracy - accuracy, 2), 0) / samples.length)
          : null,
        hasAltitude: position.coords.altitude !== null,
        fakeGpsScore: indicators.reduce((score, i) => {
          if (i.severity === 'critical') return score + 40;
          if (i.severity === 'high') return score + 25;
          if (i.severity === 'medium') return score + 15;
          return score + 5;
        }, 0),
        source: isNative ? 'capacitor' : 'browser',
        // Native mock detection results
        nativeMockDetection: nativeMockResult ? {
          isSafe: nativeMockResult.isSafe,
          riskScore: nativeMockResult.riskScore,
          mockAppsFound: nativeMockResult.mockAppsFound,
          mockSettingEnabled: nativeMockResult.mockSettingEnabled,
          rootDetected: nativeMockResult.rootDetected,
          lastLocationMocked: nativeMockResult.lastLocationMocked,
        } : null,
      };

      setLocation(loc);
      setLocationTimestamp(new Date(timestamp));
      setGettingLocation(false);

      setLocationHistory(prev => {
        const newHistory = [...prev, { latitude: loc.latitude, longitude: loc.longitude, timestamp: loc.timestamp }];
        return newHistory.slice(-GPS_CONFIG.LOCATION_HISTORY_SIZE);
      });

      if (targetLocation) {
        const dist = calculateDistance(loc.latitude, loc.longitude, targetLocation.latitude, targetLocation.longitude);
        setDistance(dist);
        setInRadius(dist <= targetLocation.radius_geofence);
      }

    } catch (error) {
      console.error('GPS Error:', error);
      let msg = 'Gagal mendapatkan lokasi';
      if (error.code === 1 || error.message?.includes('denied')) {
        msg = 'Izin lokasi ditolak. Aktifkan GPS di pengaturan.';
      } else if (error.code === 2 || error.message?.includes('unavailable')) {
        msg = 'Lokasi tidak tersedia. Pastikan GPS aktif.';
      } else if (error.code === 3 || error.message?.includes('timeout')) {
        msg = 'Timeout mendapatkan lokasi.';
        if (retryCount < GPS_CONFIG.MIN_RETRIES) {
          toast.warning('Timeout GPS, mencoba ulang...');
          setTimeout(() => getLocation(retryCount + 1), 1000);
          return;
        }
      }
      setLocationError(msg);
      setGettingLocation(false);
      toast.error(msg);
    }
  }, [targetLocation, collectGpsSamples, analyzeFakeGpsIndicators, isNative]);

  const isGpsValid = location && location.accuracy <= GPS_CONFIG.MAX_ACCURACY_METERS;
  const isDesktopWarning = !isMobileDevice && location?.accuracy > 200;

  return {
    location, locationError, gettingLocation, gpsRetryCount,
    gpsWarning, locationTimestamp, fakeGpsWarning, fakeGpsIndicators,
    distance, inRadius, isGpsValid, isDesktopWarning, getLocation,
    mockDetection,
  };
};
